/**
 * Per-category page identity: copy, motifs, and Tailwind-oriented styling tokens.
 * Consumed by CategoryProductsPage for distinct visuals per industry route.
 */

const pillBase =
  "mb-5 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.18em] shadow-sm backdrop-blur";

export const CATEGORY_PAGE_THEMES = {
  "real-estate": {
    patternId: "blueprint",
    heroInkClass: "text-gray-950",
    heroMutedClass: "text-gray-600",
    pageBgClass: "min-h-screen bg-[#f4efe4]",
    heroWashClass:
      "absolute inset-0 bg-gradient-to-br from-[#faf7f0] via-[#f0e9dc] to-[#e8dcc8]",
    heroBlobRightClass: "bg-amber-500/25",
    heroBlobLeftClass: "bg-[#FFD221]/20",
    dotGridOpacity: "[0.04]",
    pillClass: `${pillBase} border border-amber-900/15 bg-white/85 text-amber-950`,
    headlineAccentClass: "text-amber-800",
    headlineSuffix: "blueprint-grade clarity.",
    searchShellClass:
      "max-w-2xl rounded-[1.35rem] border border-amber-900/10 bg-white p-2 shadow-xl",
    searchInputBgClass: "bg-[#f4efe4]",
    searchRingClass: "focus:ring-amber-500",
    collageRingClass: "border-amber-200/80 shadow-[0_34px_90px_-40px_rgba(120,80,40,0.55)]",
    narrativeSectionClass:
      "border border-amber-900/10 bg-gradient-to-br from-white via-[#fdfbf7] to-amber-50/40 shadow-sm",
    narrativeKickerClass: "text-amber-700",
    partnerStripClass:
      "rounded-[2rem] border border-stone-800 bg-gradient-to-br from-stone-900 via-stone-900 to-amber-950/40 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.75)] text-stone-50",
    partnerEyebrowClass: "text-[#FFD221]",
    topicGridEyebrowClass: "text-amber-800",
    browseCardHoverClass:
      "hover:border-amber-500/70 hover:shadow-xl hover:shadow-amber-900/10",
    browseHubSectionClass:
      "rounded-3xl border border-amber-900/10 bg-gradient-to-b from-white via-[#faf8f3] to-amber-50/25 p-6 shadow-sm sm:p-8",
    subcategoriesStripClass:
      "border-b border-amber-900/10 bg-gradient-to-b from-transparent to-amber-50/20",
    subcategoriesTileClass: "border border-amber-100/90 hover:border-amber-300/60 hover:shadow-md",
    subcategoriesIntroWrapClass:
      "max-w-xl border-l-[3px] border-amber-500/90 pl-4 sm:border-l-4 sm:pl-5",
    subcategoriesHeadingExtras: "",
    subcategoriesGridExtras: "lg:gap-6",
    subcategoriesCardExtras:
      "rounded-[1.6rem] shadow-md shadow-amber-900/[0.08] hover:shadow-lg hover:shadow-amber-900/[0.12]",
    subcategoriesImageWrapExtras: "sm:h-[10.5rem]",
    subcategoriesBodyExtras: "bg-[#fdfbf8]/95",
    subcategoriesIconWrapExtras: "rounded-2xl ring-1 ring-amber-200/35",
    hubBrowseCardExtras:
      "rounded-[1.6rem] ring-1 ring-amber-900/10 shadow-[0_12px_40px_-28px_rgba(120,83,41,0.35)]",
    exploreChipHoverClass:
      "hover:border-amber-900 hover:bg-[#FFD221] hover:text-black",
    stepSectionBgClass: "bg-white/80",
    howItWorksSubtitle:
      "Due diligence is a sequence, not a vibe—each hop should feel quieter than the last.",
    howItWorksEyebrowClass: "",
    howItWorksShellClass:
      "rounded-[2rem] border border-amber-900/10 bg-gradient-to-b from-white to-amber-50/25 p-4 shadow-inner shadow-amber-900/[0.04] ring-1 ring-amber-900/5 sm:p-7",
    howItWorksCardClass:
      "rounded-2xl border border-amber-200/65 bg-white p-5 shadow-md shadow-amber-900/[0.07] ring-1 ring-amber-100/40 transition hover:border-amber-300/55 hover:shadow-lg sm:p-6",
    howItWorksCtaClass: "",
    ctaGlowClass: "shadow-[0_0_55px_-12px_rgba(229,184,0,0.65)]",
    fontHeadingClass: "font-black tracking-tight",
    mainSurfaceClass: "",
    editorialIntro:
      "In Ghana, a plot of land often carries generations of story — customary tenure, indentures, GIS searches, mortgage stress tests — all in one noisy conversation. We built this corridor so you map the maze before anyone asks for cash.",
    narrativeTitle: "Where soil meets paperwork — still human.",
    narrativePillars: [
      {
        title: "Verify the invisible",
        text: "Title trails, stool land, leases, and resale chains deserve calm comparison — not weekend panic transfers.",
      },
      {
        title: "Finance with foresight",
        text: "Mortgages and rent-to-own structures reward borrowers who stress-test fees, tenor, and early-exit clauses.",
      },
      {
        title: "Stay ahead of hotspots",
        text: "From peri-urban sprawl to short-let corridors, location alpha still wins when infrastructure timing is readable.",
      },
    ],
    pullQuote:
      '"The best deal is boring on paper — exciting only after every stamp is justified."',
    statStrip: [
      { label: "Due diligence lanes", hint: "land · titles · mortgages" },
      { label: "Accra pulse", hint: "rentals · short-let · dev sites" },
      { label: "Partner desk", hint: "banking · valuations · counsel" },
    ],
  },

  legal: {
    patternId: "columns",
    heroInkClass: "text-white",
    heroMutedClass: "text-slate-300",
    pageBgClass: "min-h-screen bg-[#0f172a]",
    heroWashClass:
      "absolute inset-0 bg-gradient-to-br from-[#0c1526] via-[#111827] to-[#1e293b]",
    heroBlobRightClass: "bg-blue-500/15",
    heroBlobLeftClass: "bg-[#FFD221]/12",
    dotGridOpacity: "[0.07]",
    pillClass: `${pillBase} border border-blue-400/25 bg-blue-950/40 text-blue-100`,
    headlineAccentClass: "text-[#FFD221]",
    headlineSuffix: "precedent you can quote.",
    searchShellClass:
      "max-w-2xl rounded-[1.35rem] border border-blue-400/25 bg-blue-950/50 p-2 shadow-xl shadow-black/40",
    searchInputBgClass: "bg-slate-900/90 text-slate-100 placeholder:text-slate-500",
    searchRingClass: "focus:ring-[#FFD221]",
    collageRingClass: "border-blue-400/40 shadow-[0_40px_100px_-50px_rgba(0,0,0,0.85)]",
    narrativeSectionClass:
      "border border-blue-500/25 bg-gradient-to-br from-slate-900 via-[#142032] to-slate-950 text-slate-100 shadow-xl shadow-black/60",
    narrativeKickerClass: "text-blue-300",
    partnerStripClass:
      "rounded-[2rem] border border-blue-400/35 bg-blue-950/80 text-blue-50 shadow-[0_30px_80px_-40px_rgba(0,10,40,0.85)] backdrop-blur",
    partnerEyebrowClass: "text-[#FFD221]",
    topicGridEyebrowClass: "text-blue-400",
    browseCardHoverClass:
      "hover:border-blue-400/60 hover:shadow-xl hover:shadow-black/40",
    browseHubSectionClass:
      "rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8",
    subcategoriesStripClass: "border-b border-slate-200/80 bg-slate-100/50",
    subcategoriesTileClass: "border border-slate-200 hover:border-blue-300/55 hover:shadow-md",
    subcategoriesIntroWrapClass:
      "max-w-2xl border border-slate-300/75 bg-white/95 p-5 shadow-sm sm:p-6",
    subcategoriesHeadingExtras: "font-serif tracking-tight text-gray-900",
    subcategoriesGridExtras: "lg:gap-3 lg:grid-cols-5",
    subcategoriesCardExtras:
      "rounded-lg border-[1.5px] border-slate-300/80 bg-white shadow-inner shadow-slate-200/50 hover:border-slate-400/70",
    subcategoriesImageWrapExtras: "",
    subcategoriesBodyExtras: "bg-slate-50/80",
    subcategoriesIconWrapExtras: "rounded-md border border-slate-200/80 bg-white",
    hubBrowseCardExtras:
      "rounded-lg border-[1.5px] border-slate-400/55 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] shadow-slate-400/25",
    exploreChipHoverClass:
      "hover:border-[#FFD221] hover:bg-[#FFD221] hover:text-slate-900",
    mainSurfaceClass: "bg-slate-100",
    stepSectionBgClass: "bg-slate-950/95 text-slate-100 ring-blue-900/60",
    howItWorksSubtitle:
      "Search → hub → article → intake: evidence-friendly screens that briefing rooms actually recognise.",
    howItWorksEyebrowClass: "text-blue-200",
    howItWorksShellClass:
      "rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/[0.08] to-white/[0.02] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm sm:p-7",
    howItWorksCardClass:
      "rounded-2xl border border-blue-400/25 bg-gradient-to-br from-slate-900/90 to-slate-950 p-5 shadow-lg shadow-black/40 transition hover:border-blue-400/45 hover:-translate-y-0.5 sm:rounded-[1.35rem] sm:p-6",
    howItWorksCtaClass:
      "border-blue-400/35 bg-blue-950/50 text-blue-50 hover:border-blue-300 hover:bg-blue-900/55",
    ctaGlowClass: "shadow-[0_0_60px_-15px_rgba(255,210,33,0.45)]",
    fontHeadingClass: "font-black tracking-tight font-serif",
    editorialIntro:
      "Courts spell relief in affidavits, not vibes. Ghanaian legal life moves between statutes, customary interfaces, and the urgent reality of wrongful termination, probate queues, or a land caveat you never authorised. Enter this desk with terminology tamed.",
    narrativeTitle: "Clarity before counsel — urgency without blindness.",
    narrativePillars: [
      {
        title: "Document gravity",
        text: "From indentures to employment contracts — the footnotes often carry the knockout clauses.",
      },
      {
        title: "Forum shopping (the good kind)",
        text: "ADR, mediation, and regulator channels can unblock faster than a docket gamble.",
      },
      {
        title: "Evidence hygiene",
        text: "Timestamps, chain of custody for payments, witness statements — your future self mails thank-you notes.",
      },
    ],
    pullQuote: '"Speak with lawyers early — surprise is expensive."',
    statStrip: [
      { label: "Practice clusters", hint: "civil · commercial · labour" },
      { label: "Consumer pain", hint: "land · divorce · startups" },
      { label: "Partner lane", hint: "triage · intake · briefing" },
    ],
  },

  nutrition: {
    patternId: "organic",
    heroInkClass: "text-emerald-950",
    heroMutedClass: "text-emerald-900/75",
    pageBgClass: "min-h-screen bg-[#f0fdf7]",
    heroWashClass:
      "absolute inset-0 bg-gradient-to-br from-emerald-50 via-[#ecfdf5] to-lime-50",
    heroBlobRightClass: "bg-emerald-400/30",
    heroBlobLeftClass: "bg-lime-300/25",
    dotGridOpacity: "[0.05]",
    pillClass: `${pillBase} border border-emerald-200/70 bg-white/90 text-emerald-900`,
    headlineAccentClass: "text-emerald-600",
    headlineSuffix: "rooted in your kitchen rhythm.",
    searchShellClass:
      "max-w-2xl rounded-[2rem] border border-emerald-200/70 bg-white/95 p-2 shadow-lg shadow-emerald-900/10",
    searchInputBgClass: "bg-emerald-50/90",
    searchRingClass: "focus:ring-emerald-500",
    collageRingClass:
      "rounded-[2.5rem] border-2 border-white shadow-[0_40px_100px_-50px_rgba(22,101,52,0.55)] bg-gradient-to-br from-emerald-50 to-white",
    narrativeSectionClass:
      "rounded-[2.5rem] border border-emerald-100 bg-gradient-to-br from-white via-emerald-50/50 to-lime-50/40 shadow-inner shadow-emerald-900/5",
    narrativeKickerClass: "text-emerald-700",
    partnerStripClass:
      "rounded-[2.5rem] border border-emerald-800/20 bg-gradient-to-r from-emerald-900 via-emerald-800 to-teal-900 text-emerald-50 shadow-[0_30px_80px_-35px_rgba(6,78,59,0.65)]",
    partnerEyebrowClass: "text-lime-200",
    topicGridEyebrowClass: "text-emerald-700",
    browseCardHoverClass:
      "hover:border-emerald-400 hover:shadow-2xl hover:shadow-emerald-900/10 rounded-[2rem]",
    browseHubSectionClass:
      "rounded-[2rem] border border-emerald-100 bg-gradient-to-br from-white to-emerald-50/35 p-6 shadow-sm sm:p-8",
    subcategoriesStripClass:
      "border-b border-emerald-100/80 bg-gradient-to-r from-emerald-50/35 via-white to-lime-50/25",
    subcategoriesTileClass: "border border-emerald-100 hover:border-emerald-400/50 hover:shadow-md",
    subcategoriesIntroWrapClass:
      "mx-auto max-w-xl text-center md:mx-0 md:max-w-xl md:text-left lg:mx-auto lg:max-w-2xl lg:text-center",
    subcategoriesHeadingExtras: "",
    subcategoriesGridExtras: "lg:gap-5",
    subcategoriesCardExtras:
      "rounded-[2rem] border-2 border-emerald-100/90 bg-white shadow-lg shadow-emerald-900/[0.06] hover:shadow-emerald-900/12",
    subcategoriesImageWrapExtras: "h-36 sm:h-44 rounded-t-[1.85rem] overflow-hidden",
    subcategoriesBodyExtras: "rounded-b-[2rem] bg-gradient-to-b from-emerald-50/40 to-white",
    subcategoriesIconWrapExtras: "rounded-[1rem] shadow-sm ring-1 ring-emerald-200/50",
    hubBrowseCardExtras:
      "rounded-[2rem] ring-2 ring-emerald-100/70 shadow-emerald-900/15",
    exploreChipHoverClass:
      "hover:border-emerald-600 hover:bg-lime-200 hover:text-emerald-950",
    mainSurfaceClass: "",
    stepSectionBgClass: "bg-white/95",
    howItWorksSubtitle:
      "Small, repeatable moves beat manifestos—kitchens honour habits that survive busy weeks.",
    howItWorksEyebrowClass: "",
    howItWorksShellClass:
      "rounded-[2rem] border border-emerald-100 bg-gradient-to-br from-white via-emerald-50/30 to-lime-50/25 p-4 shadow-inner shadow-emerald-900/[0.04] sm:p-7",
    howItWorksCardClass:
      "rounded-[2rem] border-[1.5px] border-emerald-100/90 bg-white p-5 shadow-lg shadow-emerald-900/[0.06] transition hover:border-emerald-300/60 hover:-translate-y-0.5 sm:p-6",
    howItWorksCtaClass: "",
    ctaGlowClass: "shadow-[0_0_50px_-10px_rgba(34,197,94,0.45)]",
    fontHeadingClass: "font-black tracking-tight",
    editorialIntro:
      "Nutrition isn't an aesthetic — it's spreadsheets of plantain buys, fasting windows between shifts, diabetic uncles refusing bland meals, athletes chasing grams of protein beside kenkey. Ghanaian kitchens deserve science that listens to seasoning and budget.",
    narrativeTitle: "Plates change — metabolisms bargain in public.",
    narrativePillars: [
      {
        title: "Local abundance",
        text: "Kontomire, lentils, salted fish — micronutrients hiding in folklore recipes if we label them proudly.",
      },
      {
        title: "Sugar is political",
        text: "Managing diabetes near street juice stands means strategy, substitution, not shame campaigns.",
      },
      {
        title: "Athletes sweat salt",
        text: "Hydration maths change in tropics — performance fuel obeys weather, not TikTok timelines.",
      },
    ],
    pullQuote:
      '"Feed the household budget first — micronutrients love batch cooking sundays."',
    statStrip: [
      { label: "Kitchen tracks", hint: "family · pro · athlete" },
      { label: "Condition aware", hint: "diabetes · detox · vegan" },
      { label: "Studio partner", hint: "plans · recipes · SMS nudges" },
    ],
  },

  health: {
    patternId: "pulse",
    heroInkClass: "text-gray-950",
    heroMutedClass: "text-gray-600",
    pageBgClass: "min-h-screen bg-[#fff5f5]",
    heroWashClass:
      "absolute inset-0 bg-gradient-to-bl from-white via-rose-50/80 to-red-50",
    heroBlobRightClass: "bg-red-400/25",
    heroBlobLeftClass: "bg-fuchsia-300/15",
    dotGridOpacity: "[0.04]",
    pillClass: `${pillBase} border border-red-100 bg-white/90 text-red-900`,
    headlineAccentClass: "text-red-600",
    headlineSuffix: "measured — not melodramatic.",
    searchShellClass:
      "max-w-2xl rounded-[1.25rem] border border-red-100 bg-white p-2 shadow-xl shadow-red-900/15",
    searchInputBgClass: "bg-rose-50",
    searchRingClass: "focus:ring-red-500",
    collageRingClass: "border-red-100 shadow-[0_40px_100px_-50px_rgba(185,28,28,0.45)]",
    narrativeSectionClass:
      "border border-red-100 bg-gradient-to-br from-white via-rose-50/40 to-white shadow-[0_20px_60px_-40px_rgba(127,29,29,0.35)]",
    narrativeKickerClass: "text-red-600",
    partnerStripClass:
      "rounded-[2rem] border border-red-900/20 bg-gradient-to-br from-neutral-950 via-red-950/80 to-neutral-950 text-neutral-50",
    partnerEyebrowClass: "text-rose-200",
    topicGridEyebrowClass: "text-red-600",
    browseCardHoverClass:
      "hover:border-red-300 hover:shadow-xl hover:shadow-red-950/15",
    browseHubSectionClass:
      "rounded-3xl border border-red-100 bg-gradient-to-b from-white to-rose-50/45 p-6 shadow-sm sm:p-8",
    subcategoriesStripClass: "border-b border-red-100/70 bg-gradient-to-b from-white to-rose-50/30",
    subcategoriesTileClass: "border border-rose-100 hover:border-red-300/70 hover:shadow-md",
    subcategoriesIntroWrapClass:
      "relative max-w-xl before:absolute before:left-0 before:top-0 before:h-9 before:w-1 before:rounded-full before:bg-red-500 pl-4",
    subcategoriesHeadingExtras: "",
    subcategoriesGridExtras: "lg:gap-4",
    subcategoriesCardExtras:
      "rounded-xl border-2 border-red-100/90 bg-white shadow-[0_8px_30px_-16px_rgba(185,28,28,0.2)] hover:border-red-200/95",
    subcategoriesImageWrapExtras: "",
    subcategoriesBodyExtras: "border-t border-red-100/70 bg-white",
    subcategoriesIconWrapExtras: "rounded-lg bg-red-50 ring-1 ring-red-100",
    hubBrowseCardExtras: "rounded-xl ring-1 ring-red-200/60 shadow-red-900/10",
    exploreChipHoverClass:
      "hover:border-red-600 hover:bg-red-500 hover:text-white",
    mainSurfaceClass: "",
    stepSectionBgClass: "bg-rose-50/60",
    howItWorksSubtitle:
      "Clarity arrives in order—triage the question, deepen with proof, then open a humane door.",
    howItWorksEyebrowClass: "",
    howItWorksShellClass:
      "rounded-3xl border border-red-100 bg-gradient-to-b from-white to-rose-50/55 p-4 shadow-inner shadow-red-900/[0.05] ring-1 ring-red-100/40 sm:p-7",
    howItWorksCardClass:
      "rounded-2xl border-2 border-red-100/80 bg-white p-5 shadow-[0_10px_40px_-24px_rgba(185,28,28,0.18)] transition hover:border-red-200 hover:-translate-y-0.5 sm:p-6",
    howItWorksCtaClass: "",
    ctaGlowClass: "shadow-[0_0_55px_-12px_rgba(239,68,68,0.45)]",
    fontHeadingClass: "font-black tracking-tight",
    editorialIntro:
      "Healthcare choices in Ghana braid NHIS practicality with private escalation, pharmacist counsel, diaspora-remittance funded scans, and the quiet courage of mental health check-ins between church duties. Navigate cover, stigma, queues, and second opinions — without folklore replacing diagnosis.",
    narrativeTitle: "Care is logistics plus empathy.",
    narrativePillars: [
      {
        title: "Insurance maths",
        text: "Outpatient carve-outs versus inpatient caps — boring tables save catastrophic surprises.",
      },
      {
        title: "Screens beat stories",
        text: "BP cuffs at home beat guessing; mammograms trump WhatsApp rumours.",
      },
      {
        title: "Telehealth ≠ toy",
        text: "Bandwidth allowing, triage chats route people faster — if providers publish honest scopes.",
      },
    ],
    pullQuote:
      '"The body keeps receipts — listen before the ledger forces you."',
    statStrip: [
      { label: "Cover & access", hint: "insurance · pharma · labs" },
      { label: "Whole human", hint: "mind · mums · metabolism" },
      { label: "Navigator desk", hint: "routing · callbacks · QA" },
    ],
  },

  careers: {
    patternId: "orbit",
    heroInkClass: "text-violet-950",
    heroMutedClass: "text-violet-900/75",
    pageBgClass: "min-h-screen bg-[#f5f3ff]",
    heroWashClass:
      "absolute inset-0 bg-gradient-to-tr from-[#ede9fe] via-violet-50 to-fuchsia-50",
    heroBlobRightClass: "bg-purple-400/35",
    heroBlobLeftClass: "bg-fuchsia-400/25",
    dotGridOpacity: "[0.05]",
    pillClass: `${pillBase} border border-violet-200 bg-white/90 text-violet-950`,
    headlineAccentClass: "text-purple-600",
    headlineSuffix: "orbit-ready.",
    searchShellClass:
      "max-w-2xl rounded-[1rem] border border-violet-200 bg-white p-2 shadow-lg shadow-purple-900/10",
    searchInputBgClass: "bg-violet-50/80",
    searchRingClass: "focus:ring-purple-600",
    collageRingClass:
      "skew-y-0 rotate-1 border-violet-200 shadow-[0_45px_100px_-48px_rgba(91,33,182,0.55)] lg:-rotate-1",
    narrativeSectionClass:
      "border border-violet-100 bg-[linear-gradient(135deg,#ffffff_0%,#fae8ff55_48%,#ede9fe_100%)] shadow-purple-900/10 shadow-xl",
    narrativeKickerClass: "text-purple-600",
    partnerStripClass:
      "rounded-[2rem] border border-violet-950/40 bg-gradient-to-br from-[#2e1065] via-violet-900 to-purple-950 text-violet-50",
    partnerEyebrowClass: "text-fuchsia-200",
    topicGridEyebrowClass: "text-purple-600",
    browseCardHoverClass:
      "hover:border-purple-400 hover:shadow-purple-900/25 hover:-translate-y-2",
    browseHubSectionClass:
      "rounded-3xl border border-violet-200/90 bg-gradient-to-br from-white to-violet-50/55 p-6 shadow-sm sm:p-8",
    subcategoriesStripClass:
      "border-b border-violet-200/70 bg-gradient-to-r from-violet-50/40 to-fuchsia-50/25",
    subcategoriesTileClass: "border border-violet-100 hover:border-purple-400/45 hover:shadow-md",
    subcategoriesIntroWrapClass: "relative max-w-2xl pb-1 sm:max-w-3xl",
    subcategoriesHeadingExtras:
      "bg-gradient-to-r from-violet-950 to-purple-700 bg-clip-text text-transparent sm:text-4xl",
    subcategoriesGridExtras: "lg:gap-5",
    subcategoriesCardExtras:
      "rounded-2xl border border-violet-200/80 bg-gradient-to-br from-white via-white to-violet-50/50 shadow-md shadow-violet-500/10 hover:shadow-violet-500/20",
    subcategoriesImageWrapExtras: "",
    subcategoriesBodyExtras: "bg-white/80",
    subcategoriesIconWrapExtras: "rounded-xl bg-violet-100/80 ring-1 ring-violet-200/60",
    hubBrowseCardExtras:
      "rounded-2xl border border-violet-300/50 bg-gradient-to-b from-white to-violet-50/40 shadow-violet-500/15",
    exploreChipHoverClass:
      "hover:border-purple-950 hover:bg-[#FFD221] hover:text-purple-950",
    mainSurfaceClass: "",
    stepSectionBgClass: "bg-violet-50/90",
    howItWorksSubtitle:
      "Signals before noise—find your lane, read what employers reward, then reach humans who hire.",
    howItWorksEyebrowClass: "",
    howItWorksShellClass:
      "rounded-[2rem] border border-violet-200/80 bg-gradient-to-br from-white via-fuchsia-50/20 to-violet-50/40 p-4 shadow-inner shadow-purple-900/[0.05] ring-1 ring-violet-200/50 sm:p-7",
    howItWorksCardClass:
      "rounded-2xl border border-violet-200/80 bg-white p-5 shadow-lg shadow-purple-500/[0.12] transition hover:border-purple-400/50 hover:-translate-y-0.5 sm:p-6",
    howItWorksCtaClass: "",
    ctaGlowClass: "shadow-[0_0_60px_-10px_rgba(126,58,242,0.55)]",
    fontHeadingClass: "font-black tracking-tight",
    editorialIntro:
      "Talent here negotiates NSS timelines, TikTok-fed remote gigs, UK care visas, CFA nights, aunties demanding 'stable government work', and WhatsApp internships with unclear NDAs. This lane treats ambition as geography — crossing borders digitally before physically.",
    narrativeTitle: "CV arcs aren't linear — they're episodic bravery.",
    narrativePillars: [
      {
        title: "Credential arbitrage",
        text: "Stack micro-certificates that recruiters can Ctrl+F — badges beat buzzwords.",
      },
      {
        title: "Network heatmaps",
        text: "Accra breakfasts, diaspora Slack rooms, NSS cohorts — map weak ties deliberately.",
      },
      {
        title: "Founder itch",
        text: "Side projects de-risk quitting day jobs when revenue graphs speak before resignation letters.",
      },
    ],
    pullQuote:
      "\"Your next salary might be someone's referral DM — optimise relationships like infrastructure.\"",
    statStrip: [
      { label: "Mobility arcs", hint: "local · remote · abroad" },
      { label: "Skill stacks", hint: "certs · bootcamps · studio" },
      { label: "Partner hub", hint: "CV · mocks · scouts" },
    ],
  },

  "betting-gaming": {
    patternId: "arena",
    heroInkClass: "text-orange-50",
    heroMutedClass: "text-orange-200/85",
    pageBgClass: "min-h-screen bg-[#0c0a09]",
    heroWashClass:
      "absolute inset-0 bg-gradient-to-b from-[#1c1410] via-[#0c0a09] to-neutral-950",
    heroBlobRightClass: "bg-orange-600/35",
    heroBlobLeftClass: "bg-amber-500/15",
    dotGridOpacity: "[0.12]",
    pillClass: `${pillBase} border border-orange-500/35 bg-orange-950/35 text-orange-100`,
    headlineAccentClass: "text-orange-400",
    headlineSuffix: "play like you read the odds.",
    searchShellClass:
      "max-w-2xl rounded-[0.85rem] border border-orange-500/30 bg-orange-950/35 p-2 shadow-xl shadow-orange-900/35",
    searchInputBgClass: "bg-black/55 text-orange-50 placeholder:text-orange-300/70",
    searchRingClass: "focus:ring-orange-500",
    collageRingClass:
      "rounded-xl border-orange-400/35 shadow-[0_0_50px_-8px_rgba(249,115,22,0.55)]",
    narrativeSectionClass:
      "border border-orange-500/30 bg-gradient-to-br from-neutral-900 via-orange-950/40 to-neutral-950 text-orange-50",
    narrativeKickerClass: "text-orange-300",
    partnerStripClass:
      "rounded-[2rem] border border-orange-500/30 bg-gradient-to-r from-orange-950 via-stone-900 to-orange-950/95 text-orange-50 shadow-[0_30px_80px_-35px_rgba(154,52,18,0.85)] shadow-black/70",
    partnerEyebrowClass: "text-[#FFD221]",
    topicGridEyebrowClass: "text-orange-400",
    browseCardHoverClass:
      "hover:border-orange-400 hover:shadow-[0_0_40px_-10px_rgba(249,115,22,0.45)] hover:shadow-orange-600/35",
    browseHubSectionClass:
      "rounded-2xl border border-orange-200/60 bg-white p-6 shadow-md sm:p-8",
    subcategoriesStripClass:
      "border-b border-orange-200/50 bg-gradient-to-b from-stone-100/85 to-orange-50/35",
    subcategoriesTileClass: "border border-stone-200 hover:border-orange-300/65 hover:shadow-md",
    subcategoriesIntroWrapClass:
      "max-w-xl border-x border-orange-200/75 bg-orange-50/45 px-3 py-2 sm:border-x-2 sm:px-5 sm:py-3",
    subcategoriesHeadingExtras: "text-orange-950 tracking-tight",
    subcategoriesGridExtras: "lg:gap-4",
    subcategoriesCardExtras:
      "rounded-[0.75rem] border-2 border-orange-200/85 bg-gradient-to-b from-white to-orange-50/35 shadow-md shadow-orange-900/12",
    subcategoriesImageWrapExtras: "",
    subcategoriesBodyExtras: "",
    subcategoriesIconWrapExtras:
      "rounded-md border border-orange-200/90 bg-orange-50/90 ring-1 ring-orange-300/25",
    hubBrowseCardExtras:
      "rounded-[0.75rem] border-2 border-orange-400/45 shadow-[0_12px_40px_-24px_rgba(234,88,12,0.35)]",
    exploreChipHoverClass:
      "hover:border-orange-500 hover:bg-orange-500 hover:text-black",
    mainSurfaceClass: "bg-gradient-to-b from-stone-200 via-stone-100 to-orange-50/40",
    stepSectionBgClass: "bg-neutral-950 ring-orange-950/70 text-neutral-50",
    howItWorksSubtitle:
      "Rules first, hubs second—then reads that spell limits, payouts, and when to tap out.",
    howItWorksEyebrowClass: "text-orange-300",
    howItWorksShellClass:
      "rounded-2xl border border-orange-500/25 bg-gradient-to-b from-orange-950/40 to-transparent p-4 shadow-inner shadow-orange-950/35 backdrop-blur-sm sm:p-7",
    howItWorksCardClass:
      "rounded-xl border border-orange-500/30 bg-neutral-950/80 p-5 shadow-xl shadow-orange-950/35 transition hover:border-orange-400/45 hover:-translate-y-0.5 sm:p-6",
    howItWorksCtaClass:
      "border-orange-400/35 bg-orange-950/55 text-orange-50 hover:border-orange-300 hover:bg-orange-900/65",
    ctaGlowClass: "shadow-[0_0_90px_-20px_rgba(251,146,60,0.55)]",
    fontHeadingClass: "font-black tracking-tighter",
    editorialIntro:
      "Ghana’s betting graph is unmistakable: football fixtures, MoMo deposits, crash games like Aviator, and casino lobbies that never clock out — all under Gaming Commission guardrails. Infoboxx maps what people actually search (apps, bonuses, predictions, payouts) and pairs it with education, comparisons, and responsible-play rails so monetisation doesn’t cost credibility.",
    narrativeTitle: "Trend-led topics — disciplined storytelling.",
    narrativePillars: [
      {
        title: "Sports still steer the ship",
        text: "EPL, UCL, GPL — domestic appetite follows credible previews, odds literacy, and bankroll realism.",
      },
      {
        title: "Mobile money is the product",
        text: "If deposits or payouts feel opaque, the operator loses — readers deserve timing, fees, and limits in plain text.",
      },
      {
        title: "Protect the brand",
        text: "18+ messaging, addiction awareness, and licence transparency belong beside every affiliate lane.",
      },
    ],
    pullQuote:
      '"Teach the bet before touting the bonus — trust compounds when limits are visible."',
    statStrip: [
      { label: "Heatseeker topics", hint: "football · aviator · promos" },
      { label: "Money rails", hint: "MoMo · cards · withdrawals" },
      { label: "Compliance lane", hint: "GGC · age · responsible play" },
    ],
  },
};

export function getCategoryPageTheme(industryId) {
  return CATEGORY_PAGE_THEMES[industryId] ?? CATEGORY_PAGE_THEMES["real-estate"];
}
